import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDongDisplayName } from '../utils/dong';
import { parseBuildingGeometry, BuildingGeometry } from '../utils/coordinates';

interface KakaoMapProps {
  width?: string;
  height?: string;
  center?: {
    lat: number;
    lng: number;
  };
  level?: number;
  buildings?: Array<{
    id: number;
    dongNumber: number;
    dongName: string | null;
    shapeType: string;
    coordinates: unknown;
  }>;
  focusBuildingId?: number | null;
  onBuildingClick?: (buildingId: number) => void;
  onMapReady?: (map: any) => void;
}

declare global {
  interface Window {
    kakao: any;
  }
}

type BuildingInput = NonNullable<KakaoMapProps['buildings']>[number];
type ProcessedBuilding = BuildingInput & { geometry: BuildingGeometry };

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.978 };

const KakaoMap: React.FC<KakaoMapProps> = ({
  width = '100%',
  height = '400px',
  center,
  level = 3,
  buildings = [],
  focusBuildingId = null,
  onBuildingClick,
  onMapReady,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const overlaysRef = useRef<any[]>([]);
  const infoWindowsRef = useRef<any[]>([]);

  const processedBuildings = useMemo<ProcessedBuilding[]>(() => {
    if (!buildings.length) {
      return [];
    }

    return buildings
      .map(building => {
        const geometry = parseBuildingGeometry(building.shapeType, building.coordinates);
        if (!geometry) {
          return null;
        }
        return {
          ...building,
          geometry
        };
      })
      .filter((item): item is ProcessedBuilding => item !== null);
  }, [buildings]);

  const initializeMap = () => {
    if (!mapContainer.current || !window.kakao) return;

    try {
      const initialCenter = center ?? DEFAULT_CENTER;
      const options = {
        center: new window.kakao.maps.LatLng(initialCenter.lat, initialCenter.lng),
        level
      };

      const kakaoMap = new window.kakao.maps.Map(mapContainer.current, options);
      setMap(kakaoMap);
      setIsLoading(false);

      if (onMapReady) {
        onMapReady(kakaoMap);
      }
    } catch (err) {
      console.error('Error initializing Kakao Map:', err);
      setError('지도 초기화에 실패했습니다');
      setIsLoading(false);
    }
  };

  // Load Kakao Maps SDK
  useEffect(() => {
    const apiKey = import.meta.env.VITE_KAKAO_APPKEY;

    if (!apiKey || apiKey === 'your_kakao_app_key_here') {
      setError(
        '카카오맵 API 키가 설정되지 않았습니다. .env.local 파일에 VITE_KAKAO_APPKEY를 설정해주세요'
      );
      setIsLoading(false);
      return;
    }

    if (window.kakao && window.kakao.maps) {
      initializeMap();
      return;
    }

    const handleLoad = () => {
      if (window.kakao && window.kakao.maps) {
        window.kakao.maps.load(() => {
          initializeMap();
        });
      }
    };

    let scriptElement = document.querySelector<HTMLScriptElement>('script[src*="dapi.kakao.com"]');

    if (scriptElement) {
      scriptElement.addEventListener('load', handleLoad, { once: true });
      return () => scriptElement?.removeEventListener('load', handleLoad);
    }

    scriptElement = document.createElement('script');
    scriptElement.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&libraries=drawing&autoload=false`;
    scriptElement.async = true;
    scriptElement.addEventListener('load', handleLoad, { once: true });
    scriptElement.addEventListener('error', () => {
      setError('카카오맵 SDK 로드에 실패했습니다');
      setIsLoading(false);
    });
    document.head.appendChild(scriptElement);

    return () => {
      scriptElement?.removeEventListener('load', handleLoad);
    };
  }, []);

  useEffect(() => {
    if (!map || !center) return;
    map.setCenter(new window.kakao.maps.LatLng(center.lat, center.lng));
  }, [map, center?.lat, center?.lng]);

  useEffect(() => {
    if (!map) return;

    overlaysRef.current.forEach(overlay => overlay.setMap(null));
    overlaysRef.current = [];
    infoWindowsRef.current.forEach(infoWindow => infoWindow.close());
    infoWindowsRef.current = [];

    if (!processedBuildings.length) {
      return;
    }

    const bounds = new window.kakao.maps.LatLngBounds();
    let hasBounds = false;

    processedBuildings.forEach(building => {
      const path = building.geometry.points.map(
        point => new window.kakao.maps.LatLng(point.lat, point.lng)
      );
      if (path.length === 0) {
        return;
      }

      const isFocused =
        focusBuildingId !== null && focusBuildingId !== undefined
          ? building.id === focusBuildingId
          : false;

      const polygon = new window.kakao.maps.Polygon({
        path,
        strokeWeight: isFocused ? 3 : 2,
        strokeColor: isFocused ? '#ef4444' : '#2563eb',
        strokeOpacity: 0.9,
        fillColor: isFocused ? '#ef4444' : '#2563eb',
        fillOpacity: isFocused ? 0.4 : 0.25,
      });

      polygon.setMap(map);
      overlaysRef.current.push(polygon);

      const displayName = formatDongDisplayName(building.dongNumber, building.dongName);
      const infoWindow = new window.kakao.maps.InfoWindow({
        content: `<div style="padding:5px 8px;font-size:12px;">
          <div style="font-weight:600;">${displayName}</div>
          <div style="font-size:11px;color:#6b7280;">${building.shapeType}</div>
        </div>`
      });
      infoWindowsRef.current.push(infoWindow);

      window.kakao.maps.event.addListener(polygon, 'click', () => {
        infoWindowsRef.current.forEach(windowInstance => windowInstance.close());
        infoWindow.setPosition(
          new window.kakao.maps.LatLng(
            building.geometry.center.lat,
            building.geometry.center.lng
          )
        );
        infoWindow.open(map);
        if (onBuildingClick) {
          onBuildingClick(building.id);
        }
      });

      path.forEach(point => {
        bounds.extend(point);
        hasBounds = true;
      });
    });

    if (!focusBuildingId && hasBounds) {
      map.setBounds(bounds);
    }
  }, [map, processedBuildings, focusBuildingId, onBuildingClick]);

  useEffect(() => {
    if (!map || focusBuildingId === null || focusBuildingId === undefined) {
      return;
    }
    const target = processedBuildings.find(building => building.id === focusBuildingId);
    if (!target) {
      return;
    }

    const bounds = new window.kakao.maps.LatLngBounds();
    target.geometry.points.forEach(point => {
      bounds.extend(new window.kakao.maps.LatLng(point.lat, point.lng));
    });
    map.setBounds(bounds);
  }, [map, focusBuildingId, processedBuildings]);

  if (error) {
    return (
      <div
        style={{ width, height }}
        className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded"
      >
        <div className="text-center p-4">
          <p className="text-red-600 mb-2">⚠️ 지도 오류</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{ width, height }}
        className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">카카오맵을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={mapContainer}
      style={{ width, height }}
      className="border border-gray-300 rounded"
    />
  );
};

export default KakaoMap;
